import { normalizePublicWebsiteUrl } from "@/lib/websiteUrl";
import {
  ADS_BILLING_COUNTRY_OPTIONS,
  isAdsBillingCountryCode,
} from "@/routes/adManager/AdsBillingUtils";

import type { components } from "@oai/sofa-client/components";

export type AdvertiserType = "business" | "individual";
export type AgencyAnswer = "yes" | "no";

export type OnboardingFormState = {
  selectedTenantId: string;
  businessName: string;
  businessWebsite: string;
  industryName: string;
  countryCode: string;
  currencyCode: string;
  timezone: string;
  advertiserType: AdvertiserType;
  agencyAnswer: AgencyAnswer;
};

export type OnboardingFormPatch = Partial<OnboardingFormState>;

type SofaCreateOnboardingAdAccountBody = components["schemas"]["CreateUiOnboardingAdAccountBody"];
type SofaAdsOnboardingIntentResource = components["schemas"]["AdsOnboardingIntentResource"];

export type CreateOnboardingAdAccountBody = SofaCreateOnboardingAdAccountBody;
export type AdsOnboardingIntentResource = SofaAdsOnboardingIntentResource;

export type OnboardingLockedFields = {
  businessName: boolean;
  countryCode: boolean;
  currencyCode: boolean;
};

export type TaxIdCollectionPolicy = {
  isEnabled: boolean;
  maxCount: number;
};

export const ONBOARDING_BUSINESS_NAME_MIN_LENGTH = 3;
export const ONBOARDING_BUSINESS_NAME_MAX_LENGTH = 120;
export const ONBOARDING_BUSINESS_WEBSITE_MAX_LENGTH = 2048;
export const ONBOARDING_BUSINESS_WEBSITE_FORMAT_ERROR =
  "Enter a valid, well-formatted business website. ex. https://www.example.com";
export const DEFAULT_ONBOARDING_COUNTRY_CODE = "US";
export const DEFAULT_ONBOARDING_COUNTRY_LABEL = "United States";
export const DEFAULT_ONBOARDING_CURRENCY_CODE = "USD";
export const ADS_MANAGER_SUPPORTED_COUNTRIES_ONBOARDING_CONFIG =
  "ads_manager_supported_countries_onboarding";
export const DEFAULT_TAX_ID_COLLECTION_POLICY: TaxIdCollectionPolicy = {
  isEnabled: false,
  maxCount: 0,
};
export const DEFAULT_ONBOARDING_LOCKED_FIELDS: OnboardingLockedFields = {
  businessName: false,
  countryCode: false,
  currencyCode: false,
};
const ONBOARDING_WEBSITE_URL_ERROR_MESSAGES = {
  unsupportedProtocol: "Unsupported onboarding website protocol.",
  credentials: "Onboarding website URL must not include credentials.",
  invalidHostname: "Onboarding website URL must use a valid hostname.",
  publicHostname: "Onboarding website URL must use a public hostname.",
};

export type OnboardingSupportedCountry = {
  countryCode: string;
  countryLabel: string;
  allowedCurrencyCodes: string[];
  allowedSalesLedCurrencyCodes?: string[];
  isSelfServeAllowed: boolean;
  isSalesLedOnboardingAllowed: boolean;
  isIndividualOnboardingAllowed: boolean;
  taxIdCollection?: TaxIdCollectionPolicy;
};

export type OnboardingAvailabilityHoldReason =
  | "unsupported_country"
  | "individual_onboarding_unavailable";

export function isOnboardingAvailabilityHoldReason(
  value: unknown,
): value is OnboardingAvailabilityHoldReason {
  return value === "unsupported_country" || value === "individual_onboarding_unavailable";
}

export function buildCreateOnboardingAdAccountBody({
  form,
  effectiveTenantId,
  shouldCreateTenant,
  prefillId,
  creationEntryPoint,
  promotionToken,
  prospectOnboardingToken,
  deferAdvertiserTypeSelection = false,
}: {
  form: OnboardingFormState;
  effectiveTenantId: string;
  shouldCreateTenant: boolean;
  prefillId?: string | null;
  creationEntryPoint?: NonNullable<CreateOnboardingAdAccountBody["creation_entry_point"]>;
  promotionToken?: string | null;
  prospectOnboardingToken?: string | null;
  deferAdvertiserTypeSelection?: boolean;
}): CreateOnboardingAdAccountBody {
  const websiteUrl = normalizeOnboardingWebsiteUrl(form.businessWebsite);
  const normalizedCountryCode = form.countryCode.trim().toUpperCase();
  const normalizedCurrencyCode = form.currencyCode.trim().toUpperCase();

  return {
    ...(prefillId ? { prefill_id: prefillId } : {}),
    ...(creationEntryPoint ? { creation_entry_point: creationEntryPoint } : {}),
    ...(promotionToken ? { promotion_token: promotionToken } : {}),
    ...(prospectOnboardingToken ? { prospect_onboarding_token: prospectOnboardingToken } : {}),
    ...(shouldCreateTenant ?
      { tenant_name: form.businessName.trim(), create_new_tenant: true }
    : { tenant_id: effectiveTenantId }),
    ad_account_name: form.businessName.trim(),
    url: websiteUrl,
    country_code: normalizedCountryCode,
    currency_code: normalizedCurrencyCode,
    industry_name: form.industryName.trim(),
    timezone: form.timezone,
    ...(deferAdvertiserTypeSelection ?
      { defer_advertiser_type_selection: true }
    : {
        advertiser_type: form.advertiserType,
        is_advertising_agency: form.agencyAnswer === "yes",
      }),
  };
}

export function getAdsOnboardingIntentFormPatch({
  intent,
  supportedCountries,
}: {
  intent: AdsOnboardingIntentResource;
  supportedCountries: OnboardingSupportedCountry[];
}): OnboardingFormPatch {
  const legalEntityName = intent.legal_entity_name.trim();
  const normalizedCountryCode = getAdsOnboardingIntentAdvertiserCountryCode(intent);
  const supportedCountry = supportedCountries.find(
    (country) => country.countryCode === normalizedCountryCode,
  );
  const normalizedCurrency = getAdsOnboardingIntentCurrencyCode(intent);

  return {
    ...(legalEntityName ? { businessName: legalEntityName } : {}),
    ...(supportedCountry ?
      {
        countryCode: normalizedCountryCode,
      }
    : { countryCode: "" }),
    ...(normalizedCurrency ? { currencyCode: normalizedCurrency } : {}),
    agencyAnswer: "no",
  };
}

export function getAdsOnboardingIntentLockedFields({
  intent,
  supportedCountries,
}: {
  intent: AdsOnboardingIntentResource | null;
  supportedCountries: OnboardingSupportedCountry[];
}): OnboardingLockedFields {
  if (!intent) {
    return DEFAULT_ONBOARDING_LOCKED_FIELDS;
  }

  const patch = getAdsOnboardingIntentFormPatch({ intent, supportedCountries });
  return {
    businessName: patch.businessName != null,
    countryCode: false,
    currencyCode: getAdsOnboardingIntentCurrencyCode(intent) != null,
  };
}

export function getAdsOnboardingIntentCurrencyCode(
  intent: AdsOnboardingIntentResource,
): string | null {
  const normalizedCurrency = intent.currency?.trim().toUpperCase();
  return normalizedCurrency ? normalizedCurrency : null;
}

export function getAdsOnboardingIntentSelectableCountries({
  intent,
  supportedCountries,
}: {
  intent: AdsOnboardingIntentResource;
  supportedCountries: OnboardingSupportedCountry[];
}): OnboardingSupportedCountry[] {
  const currencyCode = getAdsOnboardingIntentCurrencyCode(intent);
  if (currencyCode == null) {
    return supportedCountries;
  }
  return supportedCountries.filter((country) =>
    country.allowedCurrencyCodes.includes(currencyCode),
  );
}

export function getAdsOnboardingIntentAdvertiserCountryCode(
  intent: AdsOnboardingIntentResource,
): string {
  return (
    intent.advertiser_country_code?.trim().toUpperCase() || intent.country_code.trim().toUpperCase()
  );
}

export function getAdsOnboardingIntentBillingCountryCode(
  intent: AdsOnboardingIntentResource,
): string {
  return (
    intent.billing_country_code?.trim().toUpperCase() || intent.country_code.trim().toUpperCase()
  );
}

export function getOnboardingCountryLabel(countryCode: string | null | undefined): string | null {
  const normalizedCountryCode = countryCode?.trim().toUpperCase();
  if (!normalizedCountryCode || !isAdsBillingCountryCode(normalizedCountryCode)) {
    return null;
  }

  return (
    ADS_BILLING_COUNTRY_OPTIONS.find((option) => option.value === normalizedCountryCode)?.label ??
    normalizedCountryCode
  );
}

export function getDefaultOnboardingCountryCode({
  userCountryCode,
  supportedCountries,
}: {
  userCountryCode?: string;
  supportedCountries?: OnboardingSupportedCountry[];
}): string {
  const normalizedCountryCode = userCountryCode?.trim().toUpperCase();
  const supportedCountryCodeSet = new Set(
    (supportedCountries ?? [getDefaultOnboardingSupportedCountry()]).map(
      (country) => country.countryCode,
    ),
  );
  return (
      normalizedCountryCode &&
        isAdsBillingCountryCode(normalizedCountryCode) &&
        supportedCountryCodeSet.has(normalizedCountryCode)
    ) ?
      normalizedCountryCode
    : DEFAULT_ONBOARDING_COUNTRY_CODE;
}

export function getSupportedOnboardingCountries(
  configCountryAllowlist: unknown,
): OnboardingSupportedCountry[] {
  const rawCountryAllowlist = Array.isArray(configCountryAllowlist) ? configCountryAllowlist : [];
  const normalizedCountries = rawCountryAllowlist.flatMap((value) => {
    if (value == null || typeof value !== "object") {
      return [];
    }

    const candidate = value as {
      country_code?: unknown;
      country_label?: unknown;
      allowed_currency_codes?: unknown;
      allowed_sales_led_currency_codes?: unknown;
      is_self_serve_allowed?: unknown;
      is_sales_led_onboarding_allowed?: unknown;
      is_individual_onboarding_allowed?: unknown;
      tax_id_collection?: unknown;
    };

    const countryCode =
      typeof candidate.country_code === "string" ? candidate.country_code.trim().toUpperCase() : "";
    const countryLabel =
      typeof candidate.country_label === "string" ? candidate.country_label.trim() : "";
    const allowedCurrencyCodes =
      Array.isArray(candidate.allowed_currency_codes) ?
        Array.from(
          new Set(
            candidate.allowed_currency_codes
              .filter((currencyCode): currencyCode is string => typeof currencyCode === "string")
              .map((currencyCode) => currencyCode.trim().toUpperCase())
              .filter((currencyCode) => currencyCode.length > 0),
          ),
        )
      : [];
    const allowedSalesLedCurrencyCodes =
      Array.isArray(candidate.allowed_sales_led_currency_codes) ?
        Array.from(
          new Set(
            candidate.allowed_sales_led_currency_codes
              .filter((currencyCode): currencyCode is string => typeof currencyCode === "string")
              .map((currencyCode) => currencyCode.trim().toUpperCase())
              .filter((currencyCode) => currencyCode.length > 0),
          ),
        )
      : undefined;
    const isSelfServeAllowed =
      typeof candidate.is_self_serve_allowed === "boolean" ?
        candidate.is_self_serve_allowed
      : false;
    const isSalesLedOnboardingAllowed =
      typeof candidate.is_sales_led_onboarding_allowed === "boolean" ?
        candidate.is_sales_led_onboarding_allowed
      : false;
    const isIndividualOnboardingAllowed =
      typeof candidate.is_individual_onboarding_allowed === "boolean" ?
        candidate.is_individual_onboarding_allowed
      : false;
    const taxIdCollection = getTaxIdCollectionPolicy(candidate.tax_id_collection);

    return (
        isAdsBillingCountryCode(countryCode) &&
          countryLabel.length > 0 &&
          allowedCurrencyCodes.length > 0
      ) ?
        [
          {
            countryCode,
            countryLabel,
            allowedCurrencyCodes,
            ...(allowedSalesLedCurrencyCodes != null ? { allowedSalesLedCurrencyCodes } : {}),
            isSelfServeAllowed,
            isSalesLedOnboardingAllowed,
            isIndividualOnboardingAllowed,
            taxIdCollection,
          },
        ]
      : [];
  });

  const countryByCode = new Map<string, OnboardingSupportedCountry>();
  for (const country of normalizedCountries) {
    countryByCode.set(country.countryCode, country);
  }
  return countryByCode.size > 0 ?
      Array.from(countryByCode.values())
    : [getDefaultOnboardingSupportedCountry()];
}

export function getSelfServeOnboardingCountryCodes(
  supportedCountries: OnboardingSupportedCountry[],
): string[] {
  return supportedCountries
    .filter((country) => country.isSelfServeAllowed)
    .map((country) => country.countryCode);
}

export function getSalesLedOnboardingCountries(
  supportedCountries: OnboardingSupportedCountry[],
): OnboardingSupportedCountry[] {
  return supportedCountries
    .filter((country) => country.isSalesLedOnboardingAllowed === true)
    .map((country) => ({
      ...country,
      allowedCurrencyCodes: country.allowedSalesLedCurrencyCodes ?? country.allowedCurrencyCodes,
    }));
}

export function getTaxIdCollectionPolicyForCountry({
  countryCode,
  supportedCountries,
}: {
  countryCode: string;
  supportedCountries: OnboardingSupportedCountry[];
}): TaxIdCollectionPolicy {
  const normalizedCountryCode = countryCode.trim().toUpperCase();
  return (
    supportedCountries.find((country) => country.countryCode === normalizedCountryCode)
      ?.taxIdCollection ?? DEFAULT_TAX_ID_COLLECTION_POLICY
  );
}

function getTaxIdCollectionPolicy(rawTaxIdCollection: unknown): TaxIdCollectionPolicy {
  if (rawTaxIdCollection == null || typeof rawTaxIdCollection !== "object") {
    return DEFAULT_TAX_ID_COLLECTION_POLICY;
  }

  const candidate = rawTaxIdCollection as {
    is_enabled?: unknown;
    max_count?: unknown;
  };
  const isEnabled = candidate.is_enabled === true;
  const maxCount =
    typeof candidate.max_count === "number" && Number.isInteger(candidate.max_count) ?
      Math.max(candidate.max_count, 0)
    : 0;

  return {
    isEnabled,
    maxCount,
  };
}

export function getOnboardingAvailabilityHoldReason({
  countryCode,
  advertiserType,
  supportedCountries,
}: {
  countryCode: string;
  advertiserType: AdvertiserType | string | null | undefined;
  supportedCountries: OnboardingSupportedCountry[];
}): OnboardingAvailabilityHoldReason | null {
  const normalizedCountryCode = countryCode.trim().toUpperCase();
  const selectedCountry = supportedCountries.find(
    (country) => country.countryCode === normalizedCountryCode,
  );

  if (selectedCountry?.isSelfServeAllowed === false) {
    return "unsupported_country";
  }

  if (advertiserType === "individual" && selectedCountry?.isIndividualOnboardingAllowed === false) {
    return "individual_onboarding_unavailable";
  }

  return null;
}

export function getSupportedOnboardingCurrencyCodes({
  countryCode,
  supportedCountries,
}: {
  countryCode: string;
  supportedCountries: OnboardingSupportedCountry[];
}): string[] {
  const normalizedCountryCode = countryCode.trim().toUpperCase();
  return (
    supportedCountries.find((country) => country.countryCode === normalizedCountryCode)
      ?.allowedCurrencyCodes ?? [DEFAULT_ONBOARDING_CURRENCY_CODE]
  );
}

export function isValidOnboardingWebsiteUrl(value: string): boolean {
  try {
    normalizeOnboardingWebsiteUrl(value);
    return true;
  } catch {
    return false;
  }
}

export function normalizeOnboardingWebsiteUrl(rawWebsite: string): string {
  return normalizePublicWebsiteUrl(rawWebsite, ONBOARDING_WEBSITE_URL_ERROR_MESSAGES);
}

export function createDefaultOnboardingFormState(options?: {
  userCountryCode?: string;
  supportedCountries?: OnboardingSupportedCountry[];
  timezone?: string;
}): OnboardingFormState {
  const countryCode = getDefaultOnboardingCountryCode(options ?? {});
  const supportedCountries = options?.supportedCountries ?? [
    getDefaultOnboardingSupportedCountry(),
  ];
  return {
    selectedTenantId: "",
    businessName: "",
    businessWebsite: "",
    industryName: "",
    countryCode,
    currencyCode: getSupportedOnboardingCurrencyCodes({
      countryCode,
      supportedCountries,
    })[0],
    timezone: options?.timezone ?? "America/Los_Angeles",
    advertiserType: "business",
    agencyAnswer: "no",
  };
}

export const DEFAULT_ONBOARDING_FORM_STATE = createDefaultOnboardingFormState();

function getDefaultOnboardingSupportedCountry(): OnboardingSupportedCountry {
  return {
    countryCode: DEFAULT_ONBOARDING_COUNTRY_CODE,
    countryLabel: DEFAULT_ONBOARDING_COUNTRY_LABEL,
    allowedCurrencyCodes: [DEFAULT_ONBOARDING_CURRENCY_CODE],
    isSelfServeAllowed: true,
    isSalesLedOnboardingAllowed: false,
    isIndividualOnboardingAllowed: false,
    taxIdCollection: DEFAULT_TAX_ID_COLLECTION_POLICY,
  };
}
